
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader,
  Play,
  Settings,
  LayersIcon,
  Zap,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import apiClient from "../utils/apiClient";
import { ActionButton } from "../ui/ActionButton";
import Toast from "../common/Toast";
import Card from "../common/Card";

const PROCESSING_CONFIG = {
  transient: {
    enabled: false,
    reason: "Feature temporarily disabled"
  },
  stlFiles: {
    enabled: false,
    reason: "STL file operations temporarily disabled",
    disabledOperations: ['addBuildings', 'addTerrain', 'addVegetation']
  }
};

const ProcessingStepCard = ({ 
  icon: Icon, 
  title, 
  buttons, 
  projectId, 
  gradientColors, 
  stepNumber, 
  disabled = false 
}) => {
  return (
    <Card 
      variant="gradient"
      title={title}
      gradient={gradientColors}
      icon={Icon}
      hover={!disabled}
      className={disabled ? 'opacity-60' : ''}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-white/80">Step {stepNumber}</span>
        {disabled && <span className="text-xs text-white/70">(Disabled)</span>}
      </div>

      {disabled && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800 font-medium">
              {title === "Transient" ? PROCESSING_CONFIG.transient.reason : PROCESSING_CONFIG.stlFiles.reason}
            </span>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {buttons.map((button, index) => {
          const isButtonDisabled = button.temporarilyDisabled || disabled;
          
          return (
            <div key={index}>
              {button.temporarilyDisabled && !disabled && (
                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-3 h-3 text-amber-600" />
                    <span className="text-xs text-amber-800">
                      Temporarily disabled
                    </span>
                  </div>
                </div>
              )}
              
              <ActionButton
                onClick={() => !isButtonDisabled && button.onClick && button.onClick(projectId)}
                variant={isButtonDisabled ? "secondary" : "primary"}
                disabled={isButtonDisabled || button.disabled || button.loading}
                className="w-full justify-between"
              >
                <span className="flex items-center gap-3">
                  {button.loading && (
                    <Loader className="w-4 h-4 animate-spin" />
                  )}
                  {button.loading ? (button.loadingText || "Processing...") : button.label}
                </span>
                {!button.loading && !isButtonDisabled && (
                  <ArrowRight className="w-4 h-4" />
                )}
              </ActionButton>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const CardGrid = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loadingStates, setLoadingStates] = useState({
    addBuildings: false,
  });
  const [toast, setToast] = useState(null);

  const handleAcquireGeodata = () => navigate(`/acquire-geodata/${projectId}`);
 
  const handleAddBuildings = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, addBuildings: true }));
      setToast({ message: "Starting STL generation...", type: "info" });

      const projectName = localStorage.getItem("projectName") || `Project_${projectId}`;
      const response = await apiClient.post(
        `/api/projects/${projectId}/generate-building-stl-files`,
        { 
          project_name: projectName,
          county: "Berlin"
        },
        { useOperational: "new" }
      );

      setToast({ message: "STL generation started!", type: "success" });
      pollSTLStatus(projectId);
    } catch (error) {
      setToast({ 
        message: "Error starting STL generation", 
        type: "error" 
      });
      console.error("Error starting STL generation:", error);
    } finally {
      setLoadingStates(prev => ({ ...prev, addBuildings: false }));
    }
  };

  const pollSTLStatus = async (projectId) => {
    try {
      const statusResponse = await apiClient.get(
        `/api/projects/${projectId}/stl-status`,
        { useOperational: "new" }
      );
      
      if (statusResponse.status === "processing") {
        setTimeout(() => pollSTLStatus(projectId), 5000);
      } else if (statusResponse.status === "done") {
        setToast({ message: "STL generation completed!", type: "success" });
      } else if (statusResponse.status === "error") {
        setToast({ 
          message: `STL generation failed: ${statusResponse.errorMessage}`,
          type: "error"
        });
      }
    } catch (error) {
      setToast({ 
        message: "Error checking STL status", 
        type: "error" 
      });
      console.error("Error checking STL status:", error);
    }
  };

  const handleAddTerrain = () => navigate(`/add-terrain/${projectId}`);
  const handleAddVegetation = () => navigate(`/add-vegetation/${projectId}`);
  const handleCheckOutputFiles = () => navigate(`/check-output-files/${projectId}`);
  const handleRunPreRayTracing = () => navigate(`/raytracying-execution/${projectId}`);
  const handleConfigureTransientResponse = () => navigate(`/configure-transient-response/${projectId}`);
  const handleRunTransientResponse = () => navigate(`/run-transient-response/${projectId}`);
  const handleRaytracingConfig = () => navigate(`/raytracying-config/${projectId}`);

  const processingSteps = [
    {
      id: "initiation",
      icon: Settings,
      title: "Initiation",
      stepNumber: "1",
      gradient: "from-blue-600 to-indigo-600",
      buttons: [
        { label: "Set Up KML", link: "/kml-setup" },
        { label: "Acquire Geodata", onClick: handleAcquireGeodata },
      ],
    },
    {
      id: "stl-files",
      icon: LayersIcon,
      title: "STL Files",
      stepNumber: "2",
      gradient: "from-indigo-600 to-purple-600",
      buttons: [
        { 
          label: "Add Buildings", 
          onClick: handleAddBuildings,
          loading: loadingStates.addBuildings,
          loadingText: "Generating STL...",
          temporarilyDisabled: !PROCESSING_CONFIG.stlFiles.enabled
        },
        { 
          label: "Add Terrain", 
          onClick: handleAddTerrain,
          temporarilyDisabled: !PROCESSING_CONFIG.stlFiles.enabled
        },
        { 
          label: "Add Vegetation", 
          onClick: handleAddVegetation,
          temporarilyDisabled: !PROCESSING_CONFIG.stlFiles.enabled
        },
        { label: "Check Output Files", onClick: handleCheckOutputFiles },
      ],
    },
    {
      id: "ray-tracing",
      icon: Zap,
      title: "Ray Tracing",
      stepNumber: "3",
      gradient: "from-purple-600 to-pink-600",
      buttons: [
        { label: "Configure Ray Tracing", onClick: handleRaytracingConfig },
        { label: "Run Pre-Ray Tracing", onClick: handleRunPreRayTracing },
      ],
    },
    {
      id: "transient",
      icon: Play,
      title: "Transient",
      stepNumber: "4",
      gradient: "from-pink-600 to-rose-600",
      disabled: !PROCESSING_CONFIG.transient.enabled, 
      buttons: [
        { label: "Configure Transient Response", onClick: handleConfigureTransientResponse },
        { label: "Run Transient Response", onClick: handleRunTransientResponse },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {processingSteps.map((step) => (
          <ProcessingStepCard
            key={step.id}
            icon={step.icon}
            title={step.title}
            stepNumber={step.stepNumber}
            gradientColors={step.gradient}
            buttons={step.buttons}
            projectId={projectId}
            disabled={step.disabled}
          />
        ))}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CardGrid;